import React, { useContext, useId } from 'react'
import { ComponentConfig, renderContext } from '@puckeditor/core'
import ShareIcons from 'breaktime-theme/src/components/ShareIcons'
import analytics from 'global/AnalyticsData'
import { ColorField } from 'breaktime-theme/src/puck/fields/ColorField'
import { cssValueField } from 'breaktime-theme/src/puck/fields/cssValueFields'
import { cssColor } from 'breaktime-theme/src/utils/cssValues'
import { WithLayout, withLayout } from 'breaktime-theme/src/puck/components/Layout'

export type PollSocialShareProps = WithLayout<{
  facebookShare?: boolean
  twitterShare?: boolean
  pinterestShare?: boolean
  emailShare?: boolean
  copyShare?: boolean
  whatsappShare?: boolean
  shareCopy?: string
  shareUrl?: string
  emailSubject?: string
  emailBody?: string
  iconColor?: string
}>

export const POLL_SOCIAL_SHARE_DEFAULT_PROPS: PollSocialShareProps = {
  facebookShare: true,
  twitterShare: true,
  pinterestShare: false,
  emailShare: true,
  copyShare: true,
  whatsappShare: false,
}

const boolField = {
  type: 'radio' as const,
  options: [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ],
}

const PuckPollSocialShareInternal: ComponentConfig<PollSocialShareProps> = {
  label: 'Social Share',
  defaultProps: POLL_SOCIAL_SHARE_DEFAULT_PROPS,
  ai: {
    fields: { iconColor: 'CSS colour, e.g. #1a1a1a. Empty = default.' },
  },
  fields: {
    facebookShare: { ...boolField, label: 'Facebook' },
    twitterShare: { ...boolField, label: 'Twitter / X' },
    pinterestShare: { ...boolField, label: 'Pinterest' },
    emailShare: { ...boolField, label: 'Email' },
    copyShare: { ...boolField, label: 'Copy Link' },
    whatsappShare: { ...boolField, label: 'WhatsApp' },
    // Written into a <style> rule (iconFillCss).
    iconColor: cssValueField('color', {
      type: 'custom',
      label: 'Icon Color (optional)',
      render: ({ value, onChange, id }: any) => (
        <ColorField
          field={{ type: 'colorPicker', label: 'Icon Color' }}
          value={value as string}
          onChange={onChange}
          id={id}
          clearable
        />
      ),
    }),
    shareCopy: { type: 'text', label: 'Share Text (optional)' },
    shareUrl: { type: 'text', label: 'Share URL (defaults to current page)' },
    emailSubject: { type: 'text', label: 'Email Subject' },
    emailBody: { type: 'textarea', label: 'Email Body' },
  },
  render: props => <PollSocialShareRender {...(props as PollSocialShareProps)} />,
}

export const PuckPollSocialShare = withLayout(PuckPollSocialShareInternal)

/**
 * The rule that recolours the share icons, or '' when there is no colour.
 * It is CSS text, so only a valid CSS colour goes in: anything else could
 * close the rule and add others to the page (BR-5693).
 */
export function iconFillCss(scopeClass: string, iconColor?: string): string {
  const color = cssColor(iconColor)
  if (!color) return ''
  return `.${scopeClass} svg [fill]:not([fill="none"]), .${scopeClass} svg path:not([fill="none"]) { fill: ${color} !important; }`
}

function PollSocialShareRender({
  facebookShare,
  twitterShare,
  pinterestShare,
  emailShare,
  copyShare,
  whatsappShare,
  shareCopy,
  shareUrl,
  emailSubject,
  emailBody,
  iconColor,
}: PollSocialShareProps) {
  const puckCtx = useContext(renderContext)
  const meta = (puckCtx.metadata ?? {}) as any
  const brk = meta.brk
  const step = meta.step

  // Legacy share events are step-type-named (step_interaction_referral_share_option,
  // …_poll_…, …_pq_…). Puck steps all report step.type 'PUCK', so the step
  // containers inject the archetype slug into slot metadata (shareEventSlug);
  // the legacy type map covers non-puck contexts.
  const SHARE_EVENT_SLUGS: Record<string, string> = {
    POLL: 'poll',
    LISTICLE: 'listicle',
    REFERRAL: 'referral',
    PERSONALITY_QUIZ: 'pq',
  }
  const handleShareClick = ({ platform }: { platform: string }) => {
    const slug =
      meta.shareEventSlug ?? SHARE_EVENT_SLUGS[step?.type as string]
    if (!slug || !step) return
    analytics.stepEvent(`step_interaction_${slug}_share_option`, step, {
      result_id: platform,
      result: platform,
    })
  }

  const scopeId = useId().replace(/:/g, '')
  const scopeClass = `puck-poll-share-${scopeId}`

  const colorCss = iconFillCss(scopeClass, iconColor)

  return (
    <div className={`${scopeClass} social-share`}>
      {colorCss && <style dangerouslySetInnerHTML={{ __html: colorCss }} />}
      <ShareIcons
        facebookShare={facebookShare}
        twitterShare={twitterShare}
        pinterestShare={pinterestShare}
        emailShare={emailShare}
        copyShare={copyShare}
        whatsappShare={whatsappShare}
        shareCopy={shareCopy}
        shareUrl={shareUrl || brk?.shareUrl}
        emailConfig={{ subject: emailSubject, body: emailBody }}
        onShareClick={handleShareClick}
      />
    </div>
  )
}
